import React, { useEffect, useMemo, useState, Suspense, lazy, createElement } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, SlidersHorizontalIcon } from 'lucide-react';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { FilterBar } from './components/FilterBar';
import { SchoolListings } from './components/SchoolListings';
import { ContentSection } from './components/ContentSection';
import { SeoContentSection } from './components/SeoContentSection';
import { Footer } from './components/Footer';
// Code-split route bundles — only the home page ships with the initial bundle.
const ArticlesPage = lazy(() => import('./pages/ArticlesPage').then(m => ({
  default: m.ArticlesPage
})));
const CollegesPage = lazy(() => import('./pages/CollegesPage').then(m => ({
  default: m.CollegesPage
})));
const ArticleDetailPage = lazy(() => import('./pages/ArticleDetailPage').then(m => ({
  default: m.ArticleDetailPage
})));
const ProgramLandingPage = lazy(() => import('./pages/ProgramLandingPage').then(m => ({
  default: m.ProgramLandingPage
})));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({
  default: m.TermsPage
})));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage').then(m => ({
  default: m.CookiePolicyPage
})));
const CANoticePage = lazy(() => import('./pages/CANoticePage').then(m => ({
  default: m.CANoticePage
})));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({
  default: m.PrivacyPolicyPage
})));
const RequestInformationPage = lazy(() => import('./pages/RequestInformationPage').then(m => ({
  default: m.RequestInformationPage
})));
const RequestInformationNewStartAssistancePage = lazy(() => import('./pages/RequestInformationNewStartAssistancePage').then(m => ({
  default: m.RequestInformationNewStartAssistancePage
})));
const ExploreBridgePage = lazy(() => import('./pages/ExploreBridgePage').then(m => ({
  default: m.ExploreBridgePage
})));
const RequestInformationV1Page = lazy(() => import('./pages/RequestInformationV1Page').then(m => ({
  default: m.RequestInformationV1Page
})));
const AreaOfStudyLandingPage = lazy(() => import('./pages/AreaOfStudyLandingPage').then(m => ({
  default: m.AreaOfStudyLandingPage
})));
const RequestInformationV3Page = lazy(() => import('./pages/RequestInformationV3Page').then(m => ({
  default: m.RequestInformationV3Page
})));
const FindDegreesBridgePage = lazy(() => import('./pages/FindDegreesBridgePage').then(m => ({
  default: m.FindDegreesBridgePage
})));
const FindDegreesDirectoryPage = lazy(() => import('./pages/FindDegreesDirectoryPage').then(m => ({
  default: m.FindDegreesDirectoryPage
})));
const ExploreDirectoryPage = lazy(() => import('./pages/ExploreDirectoryPage').then(m => ({
  default: m.ExploreDirectoryPage
})));
const ProgramInformationPage = lazy(() => import('./pages/ProgramInformationPage').then(m => ({
  default: m.ProgramInformationPage
})));
const ProgramInformationDirectoryPage = lazy(() => import('./pages/ProgramInformationDirectoryPage').then(m => ({
  default: m.ProgramInformationDirectoryPage
})));
const AdvertiserTemplatePage = lazy(() => import('./pages/AdvertiserTemplatePage').then(m => ({
  default: m.AdvertiserTemplatePage
})));
const PartnerCollegesPage = lazy(() => import('./pages/PartnerCollegesPage').then(m => ({
  default: m.PartnerCollegesPage
})));
const CrestmontUniversityPage = lazy(() => import('./pages/CrestmontUniversityPage').then(m => ({
  default: m.CrestmontUniversityPage
})));
const CrestmontProgramPage = lazy(() => import('./pages/CrestmontProgramPage').then(m => ({
  default: m.CrestmontProgramPage
})));
import { useSchoolSearch } from './hooks/useSchoolSearch';
import { useGeoLocation } from './hooks/useGeoLocation';
import { useMetaTags } from './hooks/useMetaTags';
import { useMetaPixel } from './hooks/useMetaPixel';
import { useUrlParams } from './hooks/useUrlParams';
import { areaOfStudyData } from './data/areaOfStudyData';
import { useFacebookDomainVerification } from './hooks/useFacebookDomainVerification';
import { getDefaultProgram, getAreaFromProgram, isValidProgram } from './data/filterOptions';
function FilterAccordion({
  degreeLevel,
  selectedAreaId,
  selectedProgram,
  loading,
  schoolCount,
  onDegreeLevelChange,
  onAreaStudyChange,
  onProgramChange
}: {
  degreeLevel: number;
  selectedAreaId: number;
  selectedProgram: number;
  loading: boolean;
  schoolCount: number;
  onDegreeLevelChange: (v: number) => void;
  onAreaStudyChange: (v: number) => void;
  onProgramChange: (v: number) => void;
}) {
  const [isOpen, setIsOpen] = useState(true);
  return <div className="w-full bg-white border-b border-gray-200 shadow-sm relative z-30" data-id="element-0" data-mp="3gl66yrp5auo1">
      {/* Accordion toggle — works on both mobile and desktop */}
      <div className="max-w-7xl mx-auto px-4" data-id="element-1" data-mp="2058rlhyu6sgh">
        <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between py-3 md:py-4 hover:bg-gray-50/50 transition-colors rounded-lg px-2" data-id="element-2" data-mp="203xnti5e5kpd">
          <div className="flex items-center gap-2.5" data-id="element-3" data-mp="2euwpsabq1azz">
            <SlidersHorizontalIcon className="w-4 h-4 text-[#3b82f6]" data-id="element-4" data-mp="2euem4smqbui1" />
            <span className="text-sm font-bold text-gray-900" data-id="element-5" data-mp="2jt7z8jy3tjho">
              Find Your Program
            </span>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-6" data-mp="2nwwb3etrr18q">
              {loading ? '...' : `${schoolCount} result${schoolCount !== 1 ? 's' : ''}`}
            </span>
          </div>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-7" data-mp="tjxz8qsf7wja" />
        </button>
      </div>

      {/* Collapsible content */}
      <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'}`} data-id="element-8" data-mp="1wsqzzp5ty15s">
        <div className="px-4 pb-4 pt-1 max-w-7xl mx-auto" data-id="element-9" data-mp="2apw3pii2mnxv">
          <p className="text-xs text-gray-500 mb-3" data-id="element-10" data-mp="25se4wkxrpp3l">
            Select your degree of interest, area of interest, and program to see
            matching schools.
          </p>

          <FilterBar degreeLevel={degreeLevel} areaStudy={selectedAreaId} program={selectedProgram} onDegreeLevelChange={onDegreeLevelChange} onAreaStudyChange={onAreaStudyChange} onProgramChange={onProgramChange} data-id="element-11" data-mp="2fs50kpy2ea59" />

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 pt-3 border-t border-gray-100" data-id="element-12" data-mp="33g4prlfq9shq">
            <span className="text-xs text-gray-500 flex items-center gap-1.5" data-id="element-13" data-mp="2yiw59e73xcdb">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" data-id="element-14" data-mp="3i33ev9r44ata"></span>
              All programs are accredited
            </span>
            <span className="text-xs text-gray-400" data-id="element-15" data-mp="3n27m3uuh7ygz">•</span>
            <span className="text-xs text-gray-500" data-id="element-16" data-mp="385ypl11yzz89">100% online options</span>
            <span className="text-xs text-gray-400 hidden sm:inline" data-id="element-17" data-mp="3d4jql2p1qmxr">•</span>
            <span className="text-xs text-gray-500 hidden sm:inline" data-id="element-18" data-mp="3d2p77stfb2vs">
              Financial aid eligible
            </span>
          </div>
        </div>
      </div>
    </div>;
}
export function App() {
  // Meta Business Manager domain verification tag.
  useFacebookDomainVerification();
  // Set favicon
  useEffect(() => {
    const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
    favicon.type = 'image/svg+xml';
    favicon.rel = 'icon';
    favicon.href = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='0.9em' font-size='90'>🎓</text></svg>";
    if (!document.querySelector("link[rel*='icon']")) {
      document.head.appendChild(favicon);
    }
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = useUrlParams();
  // Derive initial state from URL params or use defaults
  const initialProgram = urlParams.mProgram && isValidProgram(urlParams.mProgram) ? urlParams.mProgram : 900;
  const initialArea = urlParams.mProgram ? getAreaFromProgram(urlParams.mProgram) : 9;
  // Default the degree level filter to Bachelor's (value 3) when no
  // degreeInterest is supplied via URL params.
  const initialDegree = urlParams.degreeInterest ?? 3;
  // Derive currentPage from URL path
  const getPageFromPath = (pathname: string) => {
    const path = pathname.replace(/^\/+|\/+$/g, '');
    // Handle dynamic routes
    const pathMap: Record<string, string> = {
      'request-information': 'request-information',
      articles: 'articles',
      colleges: 'colleges',
      terms: 'terms',
      'privacy-policy': 'privacy-policy',
      'cookie-policy': 'cookie-policy',
      'ca-notice': 'ca-notice',
      'program-landing': 'program-landing',
      'partner-colleges': 'partner-colleges'
    };
    return pathMap[path] || 'compare';
  };
  const [currentPage, setCurrentPage] = useState<string>(getPageFromPath(location.pathname));
  const [currentArticle, setCurrentArticle] = useState<string | null>(null);
  const [degreeLevel, setDegreeLevel] = useState<number>(initialDegree);
  const [selectedAreaId, setSelectedAreaId] = useState<number>(initialArea);
  const [selectedProgram, setSelectedProgram] = useState<number>(initialProgram);
  // Sync currentPage when location changes (e.g. Magic Patterns route dropdown)
  useEffect(() => {
    setCurrentPage(getPageFromPath(location.pathname));
  }, [location.pathname]);
  // Sync filter state when URL params change (e.g. arriving from /find-degrees/v6)
  useEffect(() => {
    if (urlParams.mProgram && isValidProgram(urlParams.mProgram)) {
      setSelectedProgram(urlParams.mProgram);
      setSelectedAreaId(getAreaFromProgram(urlParams.mProgram));
    }
    if (urlParams.degreeInterest != null) {
      setDegreeLevel(urlParams.degreeInterest);
    }
  }, [urlParams.mProgram, urlParams.degreeInterest]);
  // Build tracking params from URL (falls back to defaults inside the hook)
  const tracking = useMemo(() => ({
    sub1: urlParams.sub1 ?? undefined,
    sub2: urlParams.sub2 ?? undefined,
    sub3: urlParams.sub3 ?? undefined
  }), [urlParams]);
  const {
    geo
  } = useGeoLocation();
  useMetaPixel();
  const {
    schools,
    loading,
    error
  } = useSchoolSearch({
    degreeInterest: degreeLevel,
    mProgram: selectedProgram,
    zipCode: geo.zipCode,
    tracking
  });
  const currentAreaData = areaOfStudyData[selectedAreaId];
  const metaConfig = useMemo(() => {
    const configs: Record<string, {
      title: string;
      description: string;
      keywords: string;
    }> = {
      compare: {
        title: `Compare Online ${currentAreaData.name} Programs | DegreeCompare`,
        description: `Compare top accredited online ${currentAreaData.name.toLowerCase()} programs. Find the best online colleges, read reviews, and explore degree options for ${new Date().getFullYear()}.`,
        keywords: `online ${currentAreaData.name.toLowerCase()} degree, accredited online programs, compare online colleges, ${currentAreaData.name.toLowerCase()} programs, online education`
      },
      colleges: {
        title: 'Explore Accredited Online Colleges | DegreeCompare',
        description: 'Browse top-rated accredited online colleges and universities. Compare programs, read reviews, and find the right school for your online degree.',
        keywords: 'online colleges, accredited universities, online degree programs, best online schools, college directory'
      },
      articles: {
        title: 'Articles & Guides for Online Students | DegreeCompare',
        description: 'Expert advice on choosing the right online program, financial aid, career planning, and more. Resources for prospective and current online students.',
        keywords: 'online education articles, college guides, financial aid tips, career planning, online degree advice'
      }
    };
    return configs[currentPage] || configs.compare;
  }, [currentPage, currentAreaData]);
  useMetaTags({
    title: metaConfig.title,
    description: metaConfig.description,
    keywords: metaConfig.keywords,
    ogTitle: metaConfig.title,
    ogDescription: metaConfig.description
  });
  const handleAreaChange = (areaId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(getDefaultProgram(areaId));
  };
  const handleProgramChange = (programId: number) => {
    // Derive the area from the program
    const newAreaId = getAreaFromProgram(programId);
    // If the area changes, update both area and program
    if (newAreaId !== selectedAreaId) {
      setSelectedAreaId(newAreaId);
    }
    setSelectedProgram(programId);
  };
  const handleNavigate = (page: string, articleSlug?: string) => {
    setCurrentPage(page);
    if (articleSlug) {
      setCurrentArticle(articleSlug);
    } else {
      setCurrentArticle(null);
    }
    // Update URL to match the current page
    let urlPath = '/';
    if (page === 'compare') {
      urlPath = '/';
    } else if (page === 'articles' && articleSlug) {
      // Article detail pages live at /articles/<slug>
      urlPath = `/articles/${articleSlug}`;
    } else if (page.startsWith('university/')) {
      urlPath = `/${page}`;
    } else {
      urlPath = `/${page}`;
    }
    navigate(urlPath);
    window.scrollTo(0, 0);
  };
  const handleNavigateToProgram = (areaId: number, programId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(programId);
    // Stay on compare page to show filtered results
    window.scrollTo(0, 0);
  };
  const handleNavigateToDegree = (degree: number) => {
    setDegreeLevel(degree);
    setSelectedAreaId(9);
    setSelectedProgram(900);
    setCurrentPage('compare');
    navigate('/');
    window.scrollTo(0, 0);
  };
  // Shared layout wrapper
  const isAdvertiserPage = location.pathname.startsWith('/universities/');
  // Explore bridge pages render their own nav + footer.
  const isBridgePage = location.pathname.startsWith('/find-degrees/') || location.pathname.startsWith('/explore/');
  // The v3 request-information template and the program-information pages
  // render their own chrome. The gated directory keeps the site layout.
  const isSelfContainedTemplate = location.pathname.startsWith('/request-information/v3') || location.pathname.startsWith('/program-information/');
  const PageLayout = ({
    children
  }: {
    children: React.ReactNode;
  }) => {
    if (isAdvertiserPage || isBridgePage || isSelfContainedTemplate) {
      return <div className="min-h-screen bg-gray-50 font-sans text-gray-900 flex flex-col" data-id="element-19" data-mp="1obrvkpn1lwgp">
          <main className="flex-grow" data-id="element-20" data-mp="1yfzwllku9y6o">{children}</main>
        </div>;
    }
    return <div className="min-h-screen bg-gray-100 font-sans text-gray-900 flex flex-col" data-id="element-21" data-mp="1tf2knqqfa0dm">
        <Header currentPage={currentPage} onNavigate={handleNavigate} onNavigateToProgram={handleNavigateToProgram} onNavigateToDegree={handleNavigateToDegree} data-id="element-22" data-mp="1og957srkrzat" />
        <main className="flex-grow" data-id="element-23" data-mp="2h4n4scybb3oh">{children}</main>
        <Footer onNavigate={handleNavigate} data-id="element-24" data-mp="2c6lilj5lao8s" />
      </div>;
  };
  const ComparePage = () => <>
      <HeroSection areaData={currentAreaData} selectedProgram={selectedProgram} degreeLevel={degreeLevel} selectedAreaId={selectedAreaId} t1Override={urlParams.t1} data-id="element-25" data-mp="27fvdm712snem" />
      <FilterAccordion degreeLevel={degreeLevel} selectedAreaId={selectedAreaId} selectedProgram={selectedProgram} loading={loading} schoolCount={schools.length} onDegreeLevelChange={setDegreeLevel} onAreaStudyChange={handleAreaChange} onProgramChange={handleProgramChange} data-id="element-26" data-mp="2zwpmlrmowq0b" />
      <SchoolListings schools={schools} loading={loading} error={error} selectedAreaId={selectedAreaId} selectedProgram={selectedProgram} onProgramChange={handleProgramChange} data-id="element-27" data-mp="y6h390ybrznu" />
      <SeoContentSection areaStudyId={selectedAreaId} topSchool={schools[0]} data-id="element-28" data-mp="ahfnstr2ro92" />
      <ContentSection areaData={currentAreaData} selectedAreaId={selectedAreaId} onNavigate={handleNavigate} data-id="element-29" data-mp="ajiny6lxyxri" />
    </>;
  return <PageLayout data-id="element-30" data-mp="kbcjoyrpd71">
      <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center" data-id="element-32" data-mp="5icta38q10fc">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" data-id="element-33" data-mp="5il6xxco49rl" />
          </div>} data-id="element-31" data-mp="kt3c62nuz7k">
        <Routes data-id="element-34" data-mp="1zw3zesrud261">
          <Route path="/" element={<ComparePage data-id="element-36" data-mp="1zvu7v0r5emy8" />} data-id="element-35" data-mp="1zwm5si87wwro" />
          <Route path="/request-information" element={<RequestInformationPage data-id="element-38" data-mp="3kf6c7qndifvd" />} data-id="element-37" data-mp="1ztzymjp8oj9z" />
          <Route path="/request-information/v1" element={<RequestInformationV1Page data-id="element-40" data-mp="3fd8oaj412ww3" />} data-id="element-39" data-mp="3fflflmvoejlt" />
          <Route path="/request-information/v3" element={<RequestInformationV3Page data-id="element-42" data-mp="3pdhqqeom5ejx" />} data-id="element-41" data-mp="3u4hjd7qp9agm" />
          <Route path="/request-information/new-start-assistance" element={<RequestInformationNewStartAssistancePage data-id="element-44" data-mp="6ygx9drgmaga" />} data-id="element-43" data-mp="3pcpv53v0ngtn" />
          {/* Explicit route so the first area landing shows up in the
              preview's route list; the dynamic route below serves every
              other <area-slug>/<version> combination. */}
          <Route path="/areas-of-study/psychology/v1" element={<AreaOfStudyLandingPage data-id="element-46" data-mp="bws7hplo2mz1" />} data-id="element-45" data-mp="byuvk439e614" />
          <Route path="/areas-of-study/psychology/v2" element={<AreaOfStudyLandingPage data-id="element-48" data-mp="2dqhm64j8nyx2" />} data-id="element-47" data-mp="2iq3zsf2zbh6e" />
          <Route path="/areas-of-study/health-care/v1" element={<AreaOfStudyLandingPage data-id="element-50" data-mp="1rw0vnnjrun3c" />} data-id="element-49" data-mp="2drsp624v12n4" />
          <Route path="/areas-of-study/:areaSlug/:version" element={<AreaOfStudyLandingPage data-id="element-52" data-mp="1wtsmsfcle56h" />} data-id="element-51" data-mp="1wvvm5pzmxanv" />
          {/* Explicit route so this template shows up in the preview's
              route list; the dynamic route below serves every other
              {brand}-{version} slug. */}
          {/* Password-gated directory of every explore template. */}
          <Route path="/explore" element={<ExploreDirectoryPage data-id="element-54" data-mp="1dwhoawysbfs3" />} data-id="element-53" data-mp="190at9z7xfoox" />
          <Route path="/explore/UAGC-v1" element={<ExploreBridgePage data-id="element-56" data-mp="1iw42p9qcn22h" />} data-id="element-55" data-mp="1dxjd048q40sa" />
          <Route path="/explore/UAGC-v2" element={<ExploreBridgePage data-id="element-58" data-mp="1iuszbb0tfwbw" />} data-id="element-57" data-mp="1ivuki8bjpz7c" />
          <Route path="/explore/UMA-v1" element={<ExploreBridgePage data-id="element-60" data-mp="1nvytzee1dto2" />} data-id="element-59" data-mp="1nuxij8t3z55t" />
          <Route path="/explore/UMA-v2" element={<ExploreBridgePage data-id="element-62" data-mp="2ubsqu4fca74v" />} data-id="element-61" data-mp="1nt4bvtihwkxt" />
          <Route path="/explore/UMA-v3" element={<ExploreBridgePage data-id="element-64" data-mp="2zacdy85o5oyt" />} data-id="element-63" data-mp="2uardftaup8jz" />
          <Route path="/explore/search-v1" element={<ExploreBridgePage data-id="element-66" data-mp="2zcgr7gpgk0br" />} data-id="element-65" data-mp="2z9kprieeitik" />
          <Route path="/explore/:bridgeSlug" element={<ExploreBridgePage data-id="element-68" data-mp="np3fg8gnmia1" />} data-id="element-67" data-mp="nmr105a3uq1q" />
          {/* Password-gated directory of every program-information page;
              listed before the dynamic route so the bare path resolves to
              the index. */}
          <Route path="/program-information" element={<ProgramInformationDirectoryPage data-id="element-70" data-mp="8xumbpdjmes5" />} data-id="element-69" data-mp="8y4enjm28y10" />
          {/* Explicit route so one example shows up in the preview's route
              list; the dynamic route serves every other combination. */}
          <Route path="/program-information/certificate-behavioral-psychology" element={<ProgramInformationPage data-id="element-72" data-mp="3r5tufcu7s5nv" />} data-id="element-71" data-mp="3w4wyov2de7us" />
          <Route path="/program-information/:programSlug" element={<ProgramInformationPage data-id="element-74" data-mp="1pcw5hs3k5x87" />} data-id="element-73" data-mp="3m5fqa5vqhfv4" />
          <Route path="/colleges" element={<CollegesPage data-id="element-76" data-mp="1kd9qpe8308xa" />} data-id="element-75" data-mp="1pbl2huhxsti5" />
          <Route path="/partner-colleges" element={<PartnerCollegesPage data-id="element-78" data-mp="1ke2o3ol5hm2r" />} data-id="element-77" data-mp="1kd01vtyrw7t6" />
          {/* Crestmont University runs its own brand-guide experience rather
              than the shared advertiser template. Listed before the generic
              :collegeSlug routes so it wins the match. */}
          <Route path="/universities/crestmont-university" element={<CrestmontUniversityPage data-id="element-80" data-mp="1xiptfndoi87m" />} data-id="element-79" data-mp="22h16e6z9syu2" />
          <Route path="/universities/crestmont-university/bba-programs" element={<CrestmontProgramPage slug="bba-programs" data-id="element-82" data-mp="1thmhhbyr4wbo" />} data-id="element-81" data-mp="1xhwv9asscr13" />
          <Route path="/universities/crestmont-university/aba-programs" element={<CrestmontProgramPage slug="aba-programs" data-id="element-84" data-mp="1ji59gnw126cj" />} data-id="element-83" data-mp="1oh8ggdvkimn5" />
          <Route path="/universities/:collegeSlug" element={<AdvertiserTemplatePage data-id="element-86" data-mp="1el55qjmnhfol" />} data-id="element-85" data-mp="1jki2bw3bq14d" />
          <Route path="/universities/:collegeSlug/:programSlug" element={<AdvertiserTemplatePage data-id="element-88" data-mp="38xlim39ybs2f" />} data-id="element-87" data-mp="3dwfyoxgjb2iu" />
          <Route path="/articles" element={<ArticlesPage onNavigate={handleNavigate} data-id="element-90" data-mp="3hevkxzjf9qg2" />} data-id="element-89" data-mp="38ywktynr0rrf" />
          <Route path="/articles/:slug" element={<ArticleDetailPage onNavigate={handleNavigate} onProgramChange={handleProgramChange} data-id="element-92" data-mp="3lew7y8kl79yq" />} data-id="element-91" data-mp="3hcj63v8ynw78" />
          <Route path="/program-landing" element={<ProgramLandingPage programId={selectedProgram} degreeLevel={degreeLevel} onProgramChange={handleProgramChange} data-id="element-94" data-mp="2yfrhd6angovs" />} data-id="element-93" data-mp="3v6jz08d8eozo" />
          <Route path="/terms" element={<TermsPage data-id="element-96" data-mp="37fo9jhw905vl" />} data-id="element-95" data-mp="37fwn7c073f7u" />
          <Route path="/cookie-policy" element={<CookiePolicyPage data-id="element-98" data-mp="3cf94zic7lw3k" />} data-id="element-97" data-mp="37iit777ftmny" />
          <Route path="/ca-notice" element={<CANoticePage data-id="element-100" data-mp="3cdfzw7h8vjxo" />} data-id="element-99" data-mp="3cgbyzz4twenp" />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-102" data-mp="mpfcdn1zftoc" />} data-id="element-101" data-mp="mqgx6j8svuj9" />
          {/* Directory of every bridge template; listed before the dynamic
              route so the bare path resolves to the index. */}
          <Route path="/find-degrees" element={<FindDegreesDirectoryPage data-id="element-104" data-mp="1zber75ja6kg" />} data-id="element-103" data-mp="20d48gnaqvlp" />
          <Route path="/find-degrees/:templateId" element={<FindDegreesBridgePage data-id="element-106" data-mp="3t9jv1du03eqe" />} data-id="element-105" data-mp="3t7pkmtgcv50k" />
        </Routes>
      </Suspense>
    </PageLayout>;
}